import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { 
  auth, 
  sendSignInLink, 
  checkSignInLink, 
  signInWithEmail,
  getUserData
} from '../services/firebase';

// Define the shape of our context
interface AuthContextType {
  currentUser: User | null;
  userData: any | null;
  loading: boolean;
  sendMagicLink: (email: string) => Promise<void>;
  signIn: () => Promise<any>;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
  user?: {
    uid: string;
    email: string;
    displayName?: string;
  };
  userSubscription?: 'free' | 'basic' | 'premium';
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  userData: null,
  loading: true,
  sendMagicLink: async () => {},
  signIn: async () => null,
  signOut: async () => {},
  isAuthenticated: false
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component to wrap the app and provide auth context
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  // Send magic link for authentication
  const sendMagicLink = async (email: string) => {
    return sendSignInLink(email);
  };

  // Sign in with email link
  const signIn = async () => {
    if (checkSignInLink()) {
      try {
        const result = await signInWithEmail();
        return result;
      } catch (error) {
        console.error("Error signing in:", error);
        throw error;
      }
    } else {
      throw new Error("Invalid sign-in link");
    }
  };

  // Sign out
  const signOut = async () => {
    return auth.signOut();
  };

  // Effect to handle auth state changes and set persistence
  useEffect(() => {
    // Set persistence to local to keep the user logged in across browser sessions
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log('Persistence set to local');
      })
      .catch((error) => {
        console.error('Error setting persistence:', error);
      });

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Fetch additional user data from Firestore
        const data = await getUserData(user.uid);
        setUserData(data);
      } else {
        setUserData(null);
      }
      
      setLoading(false);
    });

    // Check for sign-in link on component mount
    if (checkSignInLink()) {
      signIn().catch(console.error);
    }

    // Cleanup subscription
    return unsubscribe;
  }, []);

// Value to be provided by the context
  const value = {
    currentUser,
    userData,
    loading,
    sendMagicLink,
    signIn,
    signOut,
    isAuthenticated: !!currentUser,
    user: currentUser ? {
      uid: currentUser.uid,
      email: currentUser.email || '',
      displayName: currentUser.displayName || undefined
    } : undefined,
    userSubscription: userData?.subscription || 'free'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
